import { Link, Outlet, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuthStore } from '@/features/auth';
import { useLayout } from '@/contexts/LayoutContext';
import { usePlugins } from '@/contexts/PluginContext';
import { UserMenu } from '../UserMenu';
import { NotificationBell } from '@/features/notifications/components/NotificationBell';
import { DarkModeToggle } from '../DarkModeToggle';
import { TodayCalendarWidget } from '../TodayCalendarWidget';
import { useSystemSettings } from '@/contexts/SystemSettingsContext';
import { useTranslation } from 'react-i18next';
import { initArcadeSounds, getArcadeSounds } from '@/shared/utils/ArcadeSounds';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  BookmarkCheck,
  CalendarDays,
  DoorOpen,
  Package,
  Gamepad2,
} from 'lucide-react';

/**
 * SidebarLayoutVideogame Component
 * VideoGame/Arcade themed sidebar navigation layout
 *
 * Design Philosophy:
 * - Arcade-style sidebar with thick borders and pixel fonts
 * - Sticky header with utilities only (notifications, lang, user)
 * - Navigation items in sidebar with arcade button styling
 */
export default function SidebarLayoutVideogame() {
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { sidebarCollapsed, toggleSidebar } = useLayout();
  const { isPluginEnabled } = usePlugins();
  const { settings } = useSystemSettings();
  const [rainbowMode, setRainbowMode] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);

  const isActive = (path: string) => location.pathname.startsWith(path);

  // Initialize 8-bit sound effects on first user interaction
  useEffect(() => {
    const initSound = () => {
      initArcadeSounds();
      document.removeEventListener('click', initSound);
    };
    document.addEventListener('click', initSound);

    return () => {
      document.removeEventListener('click', initSound);
    };
  }, []);

  // Scroll detection for header shadow effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setHeaderScrolled(true);
      } else {
        setHeaderScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Konami Code Easter Egg: ↑↑↓↓←→←→BA
  useEffect(() => {
    let konamiCode: string[] = [];
    const konamiPattern = [
      'ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown',
      'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight',
      'b', 'a'
    ];

    const handleKeyDown = (e: KeyboardEvent) => {
      konamiCode.push(e.key);
      konamiCode = konamiCode.slice(-10);

      if (JSON.stringify(konamiCode) === JSON.stringify(konamiPattern)) {
        // ACTIVATE RAINBOW MODE!
        setRainbowMode(true);
        getArcadeSounds()?.playLevelUp();

        // Show points popup
        const points = document.createElement('div');
        points.className = 'points-popup';
        points.textContent = '+1000 🏆';
        points.style.cssText = `
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 9999;
          font-family: 'Press Start 2P', monospace;
          color: #39FF14;
          font-size: 48px;
          text-shadow: 0 0 20px #39FF14;
          animation: pointsPopup 2s ease-out forwards;
          pointer-events: none;
        `;
        document.body.appendChild(points);
        setTimeout(() => points.remove(), 2000);

        // Turn off rainbow mode after 10 seconds
        setTimeout(() => setRainbowMode(false), 10000);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Icon mapping for navigation items
  const iconMap: Record<string, React.ComponentType<{ className?: string; size?: number }>> = {
    '/bookings/my-bookings': BookmarkCheck,
    '/calendar': CalendarDays,
    '/bookings/rooms': DoorOpen,
    '/bookings/equipment': Package
  };

  // Unified Neo-Brutalism: White default, Pink active (all buttons same)
  // All navigation buttons use consistent styling

  // Build navigation items
  const baseNavItems = [];
  const pluginNavItems = [];
  // Check for 'BOOKING' plugin type (matches database seed: apps/backend/prisma/migrations/0002_seed_users.sql)
  if (isPluginEnabled('BOOKING')) {
    pluginNavItems.push(
      { path: '/bookings/my-bookings', label: t('navigation.myBookings') },
      { path: '/calendar', label: t('navigation.calendar') },
      { path: '/bookings/rooms', label: t('navigation.bookRoom') }
    );
  }
  // Check for 'BOOKING' plugin type (unified booking system)
  if (isPluginEnabled('BOOKING')) {
    pluginNavItems.push(
      { path: '/bookings/equipment', label: t('navigation.bookEquipment') }
    );
  }

  // Combine all navigation items: base + plugins
  // Note: Admin removed from sidebar (now in UserMenu)
  const navItems = [...baseNavItems, ...pluginNavItems];

  return (
    <>
      <div
        className={`game-container min-h-screen ${rainbowMode ? 'rainbow-mode' : ''}`}
        style={{
          backgroundColor: 'var(--theme-bg)',
          color: 'var(--theme-text)'
        }}
      >
        {/* Fixed Sidebar - Theme-aware (Yellow in light mode, Dark in cyberpunk) */}
        <aside
          className={`
            ${sidebarCollapsed ? 'w-20' : 'w-64'}
            fixed top-0 left-0 bottom-0 z-40
            flex flex-col
            transition-all duration-300 ease-in-out
            overflow-y-auto
          `}
        >
          {/* Logo/Brand - Clean Style */}
          <div className="h-24 flex items-center justify-center px-4">
            {settings?.navbarLogoData ? (
              <img
                src={settings.navbarLogoData}
                alt={settings.appName || 'Logo'}
                className={`transition-all duration-300 object-contain ${
                  sidebarCollapsed ? 'h-12 w-12' : (
                    settings.navbarLogoSize === 'sm' ? 'h-14 w-auto max-w-[200px]' :
                    settings.navbarLogoSize === 'lg' ? 'h-20 w-auto max-w-[200px]' :
                    'h-16 w-auto max-w-[200px]'  // md default
                  )
                }`}
              />
            ) : (
              <div className="flex items-center gap-2">
                <Gamepad2 className="h-8 w-8" style={{ color: 'var(--theme-text)' }} />
                {!sidebarCollapsed && (
                  <span
                    className="text-lg font-bold uppercase"
                    style={{
                      color: 'var(--theme-text)',
                    }}
                  >
                    {settings?.appName || 'CHAMAMA'}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Navigation Links - Neobrutalism Buttons */}
          <nav className="typo-navigation flex-1 px-3 py-4 space-y-3 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = iconMap[item.path];
              const active = isActive(item.path);

              // Unified Neo-Brutalism: White default, Pink active (all buttons same)
              const bgColor = active ? '#FFB6F3' : '#FFFFFF';
              const textColor = '#000000';
              const borderWidth = active ? '6px' : '4px';

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`typo-navigation nav-button neo-btn neo-btn-rounded flex items-center gap-2 ${
                    sidebarCollapsed ? 'justify-center' : 'justify-start'
                  } ${active ? 'active' : ''}`}
                  style={{
                    backgroundColor: bgColor,
                    color: textColor,
                    textDecoration: 'none',
                    borderWidth: borderWidth,
                    borderStyle: 'solid',
                    borderColor: '#000000',
                  }}
                  title={sidebarCollapsed ? item.label : undefined}
                  onMouseEnter={() => getArcadeSounds()?.playHover()}
                  onClick={() => getArcadeSounds()?.playClick()}
                >
                  {Icon && <Icon size={sidebarCollapsed ? 22 : 20} className="flex-shrink-0" />}
                  {!sidebarCollapsed && (
                    <span className="button-text" style={{ fontFamily: 'var(--font-pixel), monospace', fontSize: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.label}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Today's Calendar Widget - Only show when sidebar is expanded */}
          {!sidebarCollapsed && <TodayCalendarWidget />}

          {/* Collapse/Expand Button - White */}
          <button
            onClick={toggleSidebar}
            className="h-12 flex items-center justify-center transition-colors hover:bg-gray-100"
            style={{
              backgroundColor: '#FFFFFF',
              border: '4px solid #000000',
              borderLeft: 'none',
              borderRight: 'none',
              boxShadow: 'none',
              color: '#000000'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#F5F5F5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#FFFFFF';
            }}
          >
            {sidebarCollapsed ? (
              <ChevronRightIcon className="h-5 w-5" />
            ) : (
              <ChevronLeftIcon className="h-5 w-5" />
            )}
          </button>
        </aside>

        {/* Main Content Area with margin for fixed sidebar */}
        <div
          className="flex-1 flex flex-col"
          style={{
            marginLeft: sidebarCollapsed ? '5rem' : '16rem',
            transition: 'margin-left 0.3s ease-in-out'
          }}
        >
          {/* Fixed Header - Theme-aware with scroll shadow */}
          <header
            className="fixed top-0 right-0 z-30 flex-shrink-0"
            style={{
              boxShadow: headerScrolled
                ? '0 6px 0px #000000, 0 12px 24px rgba(0, 0, 0, 0.15)'
                : undefined,
              left: sidebarCollapsed ? '5rem' : '16rem',
              transition: 'left 0.3s ease-in-out, box-shadow 0.3s ease-in-out'
            }}
          >

            <div className="h-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-end items-center h-full gap-3">
                {user && (
                  <>
                    {/* Notification Bell - Icon Only (no wrapper) */}
                    <NotificationBell />

                    {/* Dark Mode Toggle - Circular moon/sun button */}
                    <DarkModeToggle />

                    {/* User Menu - Icon + Text Only (no wrapper) */}
                    <UserMenu />
                  </>
                )}
              </div>
            </div>
          </header>

          {/* Main Content - Scrollable with padding for fixed header */}
          <main className="flex-1 overflow-y-auto page-background-crt" style={{ marginTop: '5rem' }}>
            <div className="max-w-7xl mx-auto py-6 px-6">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
